import React, { useEffect, useState } from "react";
import { getAsistenciasAdmin, observarAsistenciaAdmin } from "../../../services/asistenciadocente.service";
import { BASE_URL } from "../../../utils/configAxios";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import * as XLSX from "xlsx";

export const BitacoraAsistencias = () => {
  const [asistencias, setAsistencias] = useState([]);
  
  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [asistenciaSelected, setAsistenciaSelected] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Volver a la primera página si cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [busqueda, filtroEstado, fechaDesde, fechaHasta]);

  useEffect(() => {
    cargarAsistencias();
  }, []);

  const cargarAsistencias = async () => {
    try {
      const data = await getAsistenciasAdmin();
      setAsistencias(data);
    } catch (err) {
      console.log(err);
    }
  };

  const procesarAccion = async (estado) => {
    try {
      await observarAsistenciaAdmin({
        idAsistencia: asistenciaSelected.idAsistencia,
        estado: estado,
        observacionAdmin: observacion,
      });
      setShowModal(false);
      setAsistenciaSelected(null);
      setObservacion("");
      cargarAsistencias();
    } catch (err) {
      alert("Error procesando la solicitud");
    }
  };

  const filtrados = asistencias.filter(a => {
    // Texto
    const texto = busqueda.toLowerCase();
    const pasaBusqueda = 
      a.docenteNombre?.toLowerCase().includes(texto) ||
      a.nombreGrupo?.toLowerCase().includes(texto) ||
      a.estado?.toLowerCase().includes(texto);

    // Estado
    const pasaEstado = filtroEstado === "" || a.estado === filtroEstado;

    // Rango de Fechas
    let pasaRango = true;
    if (fechaDesde || fechaHasta) {
      const fechaA = new Date(a.fecha).getTime();
      if (fechaDesde) {
        const fDesde = new Date(fechaDesde).getTime();
        if (fechaA < fDesde) pasaRango = false;
      }
      if (fechaHasta) {
        const fHasta = new Date(fechaHasta).getTime();
        // sumando 1 día (86400000ms) para que cuente el fin del día hasta o normalizar 00:00 vs timezone. Asumir exact date
        if (fechaA > (fHasta + 86400000)) pasaRango = false;
      }
    }

    return pasaBusqueda && pasaEstado && pasaRango;
  });

  // Calcular items de la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtrados.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtrados.length / itemsPerPage);

  const exportarAExcel = () => {
    if (filtrados.length === 0) {
      alert("No hay datos para exportar con los filtros actuales.");
      return;
    }

    const dataAExportar = filtrados.map(a => ({
      'Docente': a.docenteNombre,
      'Grupo': a.nombreGrupo,
      'Fecha': new Date(a.fecha).toLocaleDateString(),
      'Hora Entrada': a.horaEntrada ? new Date(a.horaEntrada).toLocaleTimeString() : '-',
      'Hora Salida': a.horaSalida ? new Date(a.horaSalida).toLocaleTimeString() : '-',
      'Horas Trabajadas': a.horasTrabajadas || '-',
      'Estado': a.estado,
      'Observación Admin': a.observacionAdmin || '-',
      'Detalles del Docente': a.detalleDocente || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataAExportar);
    
    // Auto size widths for simple beauty
    const wscols = [
        {wch:30}, {wch:25}, {wch:12}, {wch:12}, {wch:12}, {wch:15}, {wch:12}, {wch:35}, {wch:35}
    ];
    worksheet['!cols'] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte Asistencias");
    
    XLSX.writeFile(workbook, `Reporte_Asistencias_${new Date().getTime()}.xlsx`);
  };

  return (
    <ContenedorPageTemplate fondo={false}>
      <div className="p-6 w-full min-h-screen bg-slate-50">
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Bitácora de Asistencia Docente</h1>
            <p className="text-slate-500">Visualiza, aprueba y exporta los registros de asistencia.</p>
          </div>
          <button 
            onClick={exportarAExcel}
            className="mt-4 md:mt-0 flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg shadow font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            Exportar Excel
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Buscar por Docente o Grupo..."
            className="w-full rounded-lg border-slate-200 py-2.5 px-4 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />

          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="w-full rounded-lg border-slate-200 py-2.5 px-4 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-slate-600"
          >
            <option value="">Todos los estados</option>
            <option value="Pendiente">Pendientes</option>
            <option value="Aprobada">Aprobadas</option>
            <option value="Observada">Observadas</option>
            <option value="En Curso">En Curso</option>
          </select>

          <div className="flex flex-col">
            <span className="text-xs text-slate-500 mb-1 ml-1 font-medium">Desde:</span>
            <input
              type="date"
              className="w-full rounded-lg border-slate-200 py-2 px-4 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-slate-600"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <span className="text-xs text-slate-500 mb-1 ml-1 font-medium">Hasta:</span>
            <input
              type="date"
              className="w-full rounded-lg border-slate-200 py-2 px-4 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-slate-600"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Docente</th>
                <th className="px-6 py-4">Grupo</th>
                <th className="px-6 py-4">Fecha</th>
                <th className="px-6 py-4">Horas Trabajadas</th>
                <th className="px-6 py-4">Estado</th>
                <th className="px-6 py-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentItems.map((a) => (
                <tr key={a.idAsistencia} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-slate-800">{a.docenteNombre}</td>
                  <td className="px-6 py-4 text-slate-600">{a.nombreGrupo}</td>
                  <td className="px-6 py-4 text-slate-600">{new Date(a.fecha).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-slate-600">{a.horasTrabajadas ? Number(a.horasTrabajadas).toFixed(2) + " hrs" : "-"}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border
                      ${a.estado === 'Pendiente' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        a.estado === 'Aprobada' ? 'bg-green-50 text-green-700 border-green-200' :
                        a.estado === 'Observada' ? 'bg-red-50 text-red-700 border-red-200' :
                        'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {a.estado}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => { setAsistenciaSelected(a); setShowModal(true); setObservacion(a.observacionAdmin || ""); }}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                    >
                      Revisar
                    </button>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-slate-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      No se encontraron registros que coincidan con la búsqueda.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
            <div className="text-sm text-slate-500">
              Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a <span className="font-medium">{Math.min(indexOfLastItem, filtrados.length)}</span> de <span className="font-medium">{filtrados.length}</span> registros
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                Anterior
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Lógica para mostrar siempre páginas cerca de la actual
                  let startPage = Math.max(1, currentPage - 2);
                  if (startPage + 4 > totalPages) {
                    startPage = Math.max(1, totalPages - 4);
                  }
                  const pageNumber = startPage + i;
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNumber
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && asistenciaSelected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white z-10 shrink-0">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                <span className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                </span>
                Revisión: {asistenciaSelected.docenteNombre}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 uppercase text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
              >
                Cerrar
              </button>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto">
              <div className="space-y-4">
                <div className="flex justify-between border-b pb-2">
                  <h3 className="font-semibold text-slate-700">Entrada</h3>
                  <span className="text-sm font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{new Date(asistenciaSelected.horaEntrada).toLocaleTimeString()}</span>
                </div>
                {asistenciaSelected.urlCapturaEntrada ? (
                  <div className="relative group rounded-xl overflow-hidden shadow-sm border border-slate-200">
                    <img src={`${BASE_URL}/${asistenciaSelected.urlCapturaEntrada.replace(/\\/g, '/')}`} alt="Entrada" className="w-full object-cover max-h-72" />
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl max-h-72 flex items-center justify-center h-40">
                    <p className="text-sm text-slate-400 font-medium">Sin captura registrada</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between border-b pb-2">
                  <h3 className="font-semibold text-slate-700">Salida</h3>
                  <span className="text-sm font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{asistenciaSelected.horaSalida ? new Date(asistenciaSelected.horaSalida).toLocaleTimeString() : "No finalizada"}</span>
                </div>
                {asistenciaSelected.urlCapturaSalida ? (
                  <div className="relative group rounded-xl overflow-hidden shadow-sm border border-slate-200">
                    <img src={`${BASE_URL}/${asistenciaSelected.urlCapturaSalida.replace(/\\/g, '/')}`} alt="Salida" className="w-full object-cover max-h-72" />
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl max-h-72 flex items-center justify-center h-40">
                    <p className="text-sm text-slate-400 font-medium">Sin captura registrada</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 shrink-0">
              <div className="mb-4">
                <label className="block text-sm font-bold text-slate-700 mb-1">Notas dejadas por el Docente (Justificativos)</label>
                <div className="p-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 min-h-[60px] italic shadow-sm">
                  {asistenciaSelected.detalleDocente || "Ningún detalle adicional."}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-700 mb-1">Observación Interna Admin (Feedback)</label>
                <textarea
                  className="w-full rounded-xl border-slate-200 shadow-sm p-3 text-sm focus:ring-blue-500 focus:border-blue-500 resize-none h-20"
                  placeholder="Redacta la observación por la cual se aprueba/rechaza esta asistencia..."
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                ></textarea>
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => procesarAccion("Observada")}
                  className="px-6 py-2.5 bg-red-100 text-red-700 font-semibold rounded-xl hover:bg-red-200 transition-colors shadow-sm"
                >
                  Marcar "Observada"
                </button>
                <button
                  onClick={() => procesarAccion("Aprobada")}
                  className="px-6 py-2.5 bg-green-600 shadow-[0_4px_14px_0_rgba(22,163,74,0.39)] hover:shadow-[0_6px_20px_rgba(22,163,74,0.23)] hover:bg-green-700 text-white font-semibold rounded-xl transition-all"
                >
                  Autorizar y Aprobar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </ContenedorPageTemplate>
  );
};
